import type {
  VideoPlaybackContext,
  VideoPlaybackSetupParams,
  VideoVisibilitySetupParams,
} from "../../util/types";

// Keep this strict to avoid false positives on startup/control messages.
const endedEventNames = new Set([
  "ended",
  "videoended",
  "playbackend",
  "playback_end",
  "video_end",
  "completed",
  "complete",
]);
const setupVideoElementPlayback = (
  videoElement: HTMLVideoElement,
  context: VideoPlaybackContext,
) => {
  const {
    message,
    devRef,
    setOffSetSeconds,
    applyFullscreenStyles,
    videoEndListener,
    addKeyBlockListeners,
    removeKeyBlockListeners,
    addContextMenuListener,
    removeContextMenuListener,
    enterPseudoFullscreen,
    startPositionSeconds,
    endPositionSeconds,
  } = context;
  let handledEnd = false;
  let hasPlaybackStarted = !videoElement.paused;
  const isAtVideoEnd = (target: HTMLVideoElement) => {
    if (endPositionSeconds !== undefined) {
      return target.currentTime >= endPositionSeconds;
    }
    /* c8 ignore start */
    if (target.ended) return true;
    const duration = target.duration;
    if (!Number.isFinite(duration) || duration <= 0) return false;
    return target.currentTime >= duration - 0.2;
    /* c8 ignore end */
  };
  const handleVideoEnd = () => {
    /* c8 ignore next 1 */
    if (handledEnd) return;
    handledEnd = true;
    const completionOffset =
      endPositionSeconds === undefined
        ? videoElement.currentTime
        : Math.min(endPositionSeconds, videoElement.currentTime);
    if (endPositionSeconds !== undefined) {
      videoElement.pause();
      videoElement.currentTime = completionOffset;
    }
    videoEndListener(completionOffset);
    removeKeyBlockListeners(document);
    removeContextMenuListener(globalThis);
  };
  const seekToStart = () => {
    videoElement.currentTime = startPositionSeconds;
  };
  if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
    seekToStart();
  } else {
    videoElement.addEventListener("loadedmetadata", seekToStart, {
      once: true,
    });
  }
  const markPlaybackStarted = () => {
    hasPlaybackStarted = true;
  };
  const handleMaybeEnded = () => {
    if (hasPlaybackStarted && isAtVideoEnd(videoElement)) handleVideoEnd();
  };
  videoElement.addEventListener("play", markPlaybackStarted);
  if (message.options) {
    /* c8 ignore next 1 */
    devRef.current?.querySelector("input")?.focus();

    const videoCurrentTime = () => {
      const currentTime = videoElement.currentTime;
      setOffSetSeconds(Math.floor(currentTime));
      handleMaybeEnded();
    };
    videoElement.addEventListener("play", applyFullscreenStyles);
    videoElement.addEventListener("timeupdate", videoCurrentTime);
    videoElement.addEventListener("ended", handleVideoEnd);
    videoElement.addEventListener("pause", handleMaybeEnded);
    addKeyBlockListeners(document);
    addContextMenuListener(globalThis);

    return () => {
      videoElement.removeEventListener("play", applyFullscreenStyles);
      videoElement.removeEventListener("play", markPlaybackStarted);
      videoElement.removeEventListener("timeupdate", videoCurrentTime);
      videoElement.removeEventListener("ended", handleVideoEnd);
      videoElement.removeEventListener("pause", handleMaybeEnded);
      videoElement.removeEventListener("loadedmetadata", seekToStart);
      removeKeyBlockListeners(document);
      removeContextMenuListener(globalThis);
    };
  }
  videoElement.addEventListener("play", enterPseudoFullscreen);
  videoElement.addEventListener("timeupdate", handleMaybeEnded);
  videoElement.addEventListener("pause", handleMaybeEnded);
  videoElement.addEventListener("ended", handleVideoEnd);

  addKeyBlockListeners(document);
  addContextMenuListener(globalThis);
  if (!videoElement.paused) {
    enterPseudoFullscreen();
  }

  return () => {
    videoElement.removeEventListener("play", enterPseudoFullscreen);
    videoElement.removeEventListener("play", markPlaybackStarted);
    videoElement.removeEventListener("timeupdate", handleMaybeEnded);
    videoElement.removeEventListener("pause", handleMaybeEnded);
    videoElement.removeEventListener("ended", handleVideoEnd);
    videoElement.removeEventListener("loadedmetadata", seekToStart);
    removeKeyBlockListeners(document);
    removeContextMenuListener(globalThis);
  };
};

const setupIframePlayback = (
  iframeElement: HTMLIFrameElement,
  context: VideoPlaybackContext,
) => {
  const {
    message,
    devRef,
    setOffSetSeconds,
    applyFullscreenStyles,
    videoEndListener,
    addKeyBlockListeners,
    removeKeyBlockListeners,
    addContextMenuListener,
    removeContextMenuListener,
    startPositionSeconds,
    endPositionSeconds,
  } = context;
  let handledEnd = false;
  let blockersRemoved = false;
  let cancelled = false;
  let hasEnteredFullscreen = false;
  let latestOffsetSeconds = 0;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let pauseAtSegmentEnd = (_completionOffset: number) => {};
  const syncOffset = (value: unknown) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return;
    latestOffsetSeconds = value;
    setOffSetSeconds(Math.floor(value));
    if (endPositionSeconds !== undefined && value >= endPositionSeconds) {
      handleIframeEnded();
    }
  };

  if (message.options && devRef.current) {
    /* c8 ignore next 1 */
    devRef.current.querySelector("input")?.focus();
  }

  const removePlaybackBlockers = () => {
    if (blockersRemoved) return;
    blockersRemoved = true;
    removeKeyBlockListeners(document);
    removeContextMenuListener(globalThis);
  };

  // Wait for playback before resizing so the first click reaches Play.
  const handleIframePlay = () => {
    if (hasEnteredFullscreen) return;
    hasEnteredFullscreen = true;
    applyFullscreenStyles();
  };
  function handleIframeEnded() {
    if (handledEnd) return;
    handledEnd = true;
    const completionOffset =
      endPositionSeconds === undefined
        ? latestOffsetSeconds
        : Math.min(endPositionSeconds, latestOffsetSeconds);
    pauseAtSegmentEnd(completionOffset);
    videoEndListener(completionOffset);
    removePlaybackBlockers();
  }
  const bindInstanceListeners = (instance: unknown) => {
    if (cancelled) return null;
    const inst = instance as {
      video?: HTMLVideoElement;
      api?: {
        addEventListener?: (name: string, cb: () => void) => void;
        removeEventListener?: (name: string, cb: () => void) => void;
      };
      addEventListener?: (name: string, cb: () => void) => void;
      removeEventListener?: (name: string, cb: () => void) => void;
      seek?: (seconds: number) => void;
      pause?: () => void;
    };
    if (inst?.video) {
      pauseAtSegmentEnd = (completionOffset) => {
        if (endPositionSeconds === undefined) return;
        inst.video?.pause();
        if (inst.video) inst.video.currentTime = completionOffset;
      };
      const handleTimeUpdate = () => {
        syncOffset(inst.video?.currentTime);
        if (inst.video?.paused === false) handleIframePlay();
      };
      inst.video.addEventListener("play", handleIframePlay);
      inst.video.addEventListener("timeupdate", handleTimeUpdate);
      const handleVideoEnded = () => {
        syncOffset(inst.video?.currentTime);
        handleIframeEnded();
      };
      inst.video.addEventListener("ended", handleVideoEnded);
      const seekToStart = () => {
        inst.video!.currentTime = startPositionSeconds;
      };
      if (inst.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        seekToStart();
      } else {
        inst.video.addEventListener("loadedmetadata", seekToStart, {
          once: true,
        });
      }
      if (inst.video.paused === false) handleIframePlay();
      return () => {
        inst.video?.removeEventListener("play", handleIframePlay);
        inst.video?.removeEventListener("timeupdate", handleTimeUpdate);
        inst.video?.removeEventListener("ended", handleVideoEnded);
        inst.video?.removeEventListener("loadedmetadata", seekToStart);
      };
    }

    pauseAtSegmentEnd = () => {
      if (endPositionSeconds !== undefined) inst.pause?.();
    };
    inst.seek?.(startPositionSeconds);

    if (inst?.api?.addEventListener) {
      inst.api.addEventListener("play", handleIframePlay);
      inst.api.addEventListener("ended", handleIframeEnded);
      return () => {
        inst.api?.removeEventListener?.("play", handleIframePlay);
        inst.api?.removeEventListener?.("ended", handleIframeEnded);
      };
    }

    return null;
  };
  const connectVdoIframeInstance = () => {
    const vdo = (
      window as {
        VdoPlayer?: { getInstance?: (...args: unknown[]) => unknown };
      }
    ).VdoPlayer;
    if (!vdo?.getInstance) return null;

    const attempts: unknown[] = [
      iframeElement,
      iframeElement.contentWindow,
    ].filter(Boolean);

    for (const candidate of attempts) {
      try {
        const maybe = vdo.getInstance(candidate);
        if (!maybe) continue;

        if (typeof (maybe as Promise<unknown>).then === "function") {
          const cleanupRef: { fn: (() => void) | null } = { fn: null };
          (maybe as Promise<unknown>)
            .then((resolved) => {
              cleanupRef.fn = bindInstanceListeners(resolved);
            })
            .catch(() => {
              /* no-op */
            });
          return () => cleanupRef.fn?.();
        }

        const cleanup = bindInstanceListeners(maybe);
        if (cleanup) return cleanup;
      } catch {
        // try next candidate
      }
    }
    return null;
  };
  const handleIframeMessage = (event: MessageEvent) => {
    const iframeSrc = iframeElement.getAttribute("src") ?? "";
    try {
      const iframeOrigin = new URL(iframeSrc).origin;
      const expectedSource = iframeElement.contentWindow;
      if (!expectedSource) return;
      const isValidOrigin = event.origin === iframeOrigin;
      const isExpectedSource = event.source === expectedSource;
      if (!isValidOrigin || !isExpectedSource) return;
    } catch {
      return;
    }

    const dataObj =
      typeof event.data === "object" && event.data !== null
        ? (event.data as Record<string, unknown>)
        : null;
    const eventType = String(
      dataObj?.event ?? dataObj?.type ?? dataObj?.name ?? "",
    ).toLowerCase();
    const status = String(
      dataObj?.status ?? dataObj?.state ?? "",
    ).toLowerCase();
    const possibleCurrentTime =
      typeof dataObj?.currentTime === "number"
        ? dataObj.currentTime
        : typeof dataObj?.time === "number"
          ? dataObj.time
          : typeof dataObj?.position === "number"
            ? dataObj.position
            : null;
    if (possibleCurrentTime !== null) {
      syncOffset(possibleCurrentTime);
    }

    const isEndedEvent =
      endedEventNames.has(eventType) ||
      endedEventNames.has(status) ||
      eventType.endsWith(":ended") ||
      eventType.endsWith(".ended");

    if (!isEndedEvent) return;
    handleIframeEnded();
  };
  window.addEventListener("message", handleIframeMessage);
  addKeyBlockListeners(document);
  addContextMenuListener(globalThis);

  let instanceCleanup = connectVdoIframeInstance();
  let retryCount = 0;
  const maxRetries = 20;
  const retryTimer = window.setInterval(() => {
    if (instanceCleanup || retryCount >= maxRetries) {
      window.clearInterval(retryTimer);
      return;
    }
    retryCount += 1;
    instanceCleanup = connectVdoIframeInstance();
  }, 300);

  return () => {
    cancelled = true;
    window.clearInterval(retryTimer);
    instanceCleanup?.();
    removePlaybackBlockers();
    window.removeEventListener("message", handleIframeMessage);
  };
};

export const setupVideoPlaybackHandlers = ({
  devRef,
  message,
  setOffSetSeconds,
  videoEndListener,
  applyFullscreenStyles,
  startPositionSeconds,
  endPositionSeconds,
}: VideoPlaybackSetupParams) => {
  if (!devRef.current) return;

  const videoElement = devRef.current.querySelector("video");
  const iframeElement = devRef.current.querySelector<HTMLIFrameElement>(
    'iframe[data-testid="video-container"]',
  );

  const safeAddEventListener = (
    target: EventTarget | null,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => {
    /* c8 ignore next 1 */
    if (!target) return;
    try {
      target.addEventListener(type, listener, options);
      /* c8 ignore start */
    } catch (err) {
      console.warn(`Unable to add ${type} listener to target:`, err);
    }
    /* c8 ignore end */
  };
  const safeRemoveEventListener = (
    target: EventTarget | null,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) => {
    /* c8 ignore next 1 */
    if (!target) return;
    try {
      target.removeEventListener(type, listener, options);
      /* c8 ignore start */
    } catch (err) {
      console.warn(`Unable to remove ${type} listener from target:`, err);
    }
    /* c8 ignore end */
  };
  const addKeyBlockListeners = (target: EventTarget | null) => {
    safeAddEventListener(target, "keydown", handleKeyInput, true);
    safeAddEventListener(target, "keyup", handleKeyInput, true);
    safeAddEventListener(target, "keypress", handleKeyInput, true);
  };
  const removeKeyBlockListeners = (target: EventTarget | null) => {
    safeRemoveEventListener(target, "keydown", handleKeyInput, true);
    safeRemoveEventListener(target, "keyup", handleKeyInput, true);
    safeRemoveEventListener(target, "keypress", handleKeyInput, true);
  };
  const addContextMenuListener = (target: EventTarget | null) => {
    safeAddEventListener(target, "contextmenu", handleContextMenu, true);
  };
  const removeContextMenuListener = (target: EventTarget | null) => {
    safeRemoveEventListener(target, "contextmenu", handleContextMenu, true);
  };
  const handleKeyInput = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    keyboardEvent.stopImmediatePropagation();
  };

  const handleContextMenu = (event: Event) => {
    const mouseEvent = event as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();
  };
  const enterPseudoFullscreen = () => {
    if (!message.options) {
      applyFullscreenStyles();
    }
  };
  const playbackContext: VideoPlaybackContext = {
    message,
    devRef,
    setOffSetSeconds,
    videoEndListener,
    addKeyBlockListeners,
    removeKeyBlockListeners,
    addContextMenuListener,
    removeContextMenuListener,
    enterPseudoFullscreen,
    applyFullscreenStyles,
    startPositionSeconds,
    endPositionSeconds,
  };

  if (videoElement) {
    return setupVideoElementPlayback(videoElement, playbackContext);
  }

  if (iframeElement) {
    return setupIframePlayback(iframeElement, playbackContext);
  }
};

export const setupVideoVisibilityHandlers = ({
  devRef,
  wasPlayingRef,
  startPositionSeconds,
}: VideoVisibilitySetupParams) => {
  /* c8 ignore next 1 */
  if (!devRef.current) return;

  const videoElement = devRef.current.querySelector("video");
  const iframeElement = devRef.current.querySelector<HTMLIFrameElement>(
    'iframe[data-testid="video-container"]',
  );

  const attachVisibilityHandler = (videoTarget: HTMLVideoElement) => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasPlayingRef.current = !videoTarget.paused;
        if (wasPlayingRef.current) videoTarget.pause();
        return;
      }

      if (wasPlayingRef.current) {
        videoTarget.currentTime = startPositionSeconds;
        const playPromise = videoTarget.play();
        if (playPromise?.catch) {
          playPromise.catch((err) => {
            console.warn("Error resuming video playback:", err);
          });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  };

  if (videoElement) {
    return attachVisibilityHandler(videoElement);
  }
  /* c8 ignore next 1 */
  if (!iframeElement) return;

  let cancelled = false;
  let cleanup = () => {};
  let hasAttached = false;
  const attachFromPlayer = (player: unknown) => {
    if (cancelled || hasAttached) return;
    const videoTarget = (player as { video?: HTMLVideoElement })?.video;
    if (!videoTarget) return;
    hasAttached = true;
    cleanup = attachVisibilityHandler(videoTarget);
  };

  if (iframeElement) {
    const vdo = (
      window as {
        VdoPlayer?: { getInstance?: (...args: unknown[]) => unknown };
      }
    ).VdoPlayer;
    if (!vdo?.getInstance) return;

    const iframeSrc = iframeElement.getAttribute("src") ?? "";
    let playerParam: string | null = null;
    try {
      playerParam = new URL(iframeSrc).searchParams.get("player");
    } catch {
      // ignore malformed src
    }
    const attempts: unknown[] = [
      iframeElement,
      iframeElement.contentWindow,
      playerParam,
    ].filter(Boolean);
    for (const candidate of attempts) {
      try {
        const maybe = vdo.getInstance(candidate);
        if (!maybe) continue;
        if (typeof (maybe as Promise<unknown>).then === "function") {
          (maybe as Promise<unknown>).then(attachFromPlayer).catch(() => {
            /* no-op */
          });
        } else {
          attachFromPlayer(maybe);
        }
      } catch {
        // try next candidate
      }
    }
  }

  return () => {
    cancelled = true;
    cleanup();
  };
};
